import { Injectable, computed, signal } from '@angular/core';

import type { Chat } from '@hashbrownai/core';

type AnyMessage = Chat.Message<string, Chat.AnyTool>;

/** Ключ нового формата: список переписок вместо одной. */
const STORAGE_KEY = 'speaknode.assistant.chats.v1';
/** Ключ прошлого формата — одна переписка массивом. Переносим и забываем. */
const LEGACY_KEY = 'speaknode.assistant.chat.v2';

/**
 * Сколько переписок храним. Десять — это примерно рабочая неделя
 * обращений к агенту; дальше начинается архив, которому место на сервере,
 * а не в localStorage браузера.
 */
const MAX_CHATS = 10;

/** Одна переписка целиком — как её видит список истории. */
export interface AssistantConversation {
  id: string;
  /** Имя от модели («Создание теста для агента») либо первая реплика. */
  title: string;
  /** Когда в неё писали последний раз — по этому списку и сортируем. */
  updatedAt: number;
  /**
   * Имя придумала модель, а не эвристика. Пока false — просим имя после
   * первого обмена; повторно не просим, иначе каждая реплика стоила бы
   * лишнего запроса.
   */
  titledByModel?: boolean;
  messages: AnyMessage[];
}

/** Строка списка истории: без сообщений, их грузить в список незачем. */
export interface AssistantConversationSummary {
  id: string;
  title: string;
  updatedAt: number;
  messageCount: number;
  active: boolean;
}

/**
 * История переписок с агентом в localStorage.
 *
 * Зачем вообще хранить. Разговор с агентом — это не чат ради чата, а
 * рабочий след: «чем я правил этот промпт на прошлой неделе». Одна
 * переписка, которую затирает следующая, такой след стирает, и за ответом
 * приходится идти к агенту заново.
 *
 * Почему браузер, а не бэк. Переписка привязана к рабочему месту, а не к
 * аккаунту: она про то, что человек сейчас делает в этой вкладке. Когда
 * понадобится «то же самое с телефона» — появится эндпоинт, и этот сервис
 * станет его кэшем; формат к этому готов.
 */
@Injectable({ providedIn: 'root' })
export class AssistantHistoryService {
  private readonly chats = signal<AssistantConversation[]>([]);
  private readonly activeIdInternal = signal<string>('');

  readonly activeId = this.activeIdInternal.asReadonly();

  /** Список для панели: свежие сверху, без тел сообщений. */
  readonly conversations = computed<AssistantConversationSummary[]>(() => {
    const active = this.activeIdInternal();
    return [...this.chats()]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map(chat => ({
        id: chat.id,
        title: chat.title,
        updatedAt: chat.updatedAt,
        messageCount: chat.messages.length,
        active: chat.id === active,
      }));
  });

  /** Есть ли что показывать в истории, кроме пустого текущего разговора. */
  readonly hasHistory = computed(() => this.conversations().some(c => c.messageCount > 0));

  constructor() {
    this.load();
  }

  /** Сообщения активной переписки — с них начинается сессия агента. */
  activeMessages(): AnyMessage[] {
    return this.chats().find(c => c.id === this.activeIdInternal())?.messages ?? [];
  }

  /**
   * Сохранить текущее состояние активной переписки.
   *
   * Пустые не храним: открыли панель, ничего не спросили — в истории
   * такому месту нет, иначе список зарастёт пустышками.
   */
  save(messages: AnyMessage[]): void {
    const id = this.activeIdInternal();
    if (!id) return;

    this.chats.update(list => {
      const index = list.findIndex(c => c.id === id);
      if (index < 0) {
        if (!messages.length) return list;
        return trim([...list, { id, title: fallbackTitle(messages), updatedAt: now(), messages }]);
      }
      const current = list[index];
      const next = [...list];
      next[index] = {
        ...current,
        messages,
        // Заголовок от модели не перетираем черновым: он приезжает позже
        // первого обмена и точнее любой эвристики.
        title: current.title || fallbackTitle(messages),
        updatedAt: now(),
      };
      return next;
    });
    this.persist();
  }

  /** Имя разговору — его придумывает модель, увидев первый обмен. */
  setTitle(id: string, title: string): void {
    const clean = title.trim().replace(/^["'«»]+|["'«»]+$/g, '').slice(0, 60);
    if (!clean) return;
    this.chats.update(list => list.map(c => (c.id === id ? { ...c, title: clean } : c)));
    this.persist();
  }

  /** Нужно ли просить у модели имя: пустое или черновое из первой реплики. */
  needsTitle(id: string): boolean {
    const chat = this.chats().find(c => c.id === id);
    return !!chat && !chat.titledByModel;
  }

  /** Отметить, что имя пришло от модели, — больше не спрашиваем. */
  markTitled(id: string): void {
    this.chats.update(list => list.map(c => (c.id === id ? { ...c, titledByModel: true } : c)));
    this.persist();
  }

  /** Новая переписка. Возвращает её id — текущую сессию пересобирают под него. */
  startNew(): string {
    const id = uid();
    this.activeIdInternal.set(id);
    this.persist();
    return id;
  }

  /** Переключиться на переписку из истории. Отдаёт её сообщения. */
  switchTo(id: string): AnyMessage[] {
    if (!this.chats().some(c => c.id === id)) return [];
    this.activeIdInternal.set(id);
    this.persist();
    return this.activeMessages();
  }

  /** Удалить переписку. Если удалили активную — заводим пустую. */
  remove(id: string): void {
    this.chats.update(list => list.filter(c => c.id !== id));
    if (this.activeIdInternal() === id) this.activeIdInternal.set(uid());
    this.persist();
  }

  /** Забыть всё — «очистить историю» в шапке панели. */
  clearAll(): void {
    this.chats.set([]);
    this.activeIdInternal.set(uid());
    this.persist();
  }

  // ------------------------------------------------------------- хранилище

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw) as { activeId?: string; chats?: AssistantConversation[] };
        const chats = Array.isArray(data.chats) ? data.chats.filter(isConversation) : [];
        this.chats.set(trim(chats));
        this.activeIdInternal.set(data.activeId || chats[0]?.id || uid());
        return;
      }

      // Переезд с прошлого формата: одна переписка становится первой в списке.
      const legacy = localStorage.getItem(LEGACY_KEY);
      const messages = legacy ? (JSON.parse(legacy) as AnyMessage[]) : [];
      const id = uid();
      if (Array.isArray(messages) && messages.length) {
        this.chats.set([{ id, title: fallbackTitle(messages), updatedAt: now(), messages }]);
      }
      this.activeIdInternal.set(id);
      localStorage.removeItem(LEGACY_KEY);
      this.persist();
    } catch {
      // приватный режим или битый JSON — начинаем с чистого листа
      this.activeIdInternal.set(uid());
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ activeId: this.activeIdInternal(), chats: trim(this.chats()) }),
      );
    } catch {
      // quota — история не настолько важна, чтобы ронять приложение
    }
  }
}

/** Свежие десять; остальное вытесняется по дате последней правки. */
function trim(chats: AssistantConversation[]): AssistantConversation[] {
  return [...chats].sort((a, b) => b.updatedAt - a.updatedAt).slice(0, MAX_CHATS);
}

/**
 * Черновое имя, пока не ответила модель: первая реплика человека.
 * Лучше, чем «Новый чат», и заметно хуже того, что придумает модель.
 */
function fallbackTitle(messages: AnyMessage[]): string {
  const first = messages.find(m => m.role === 'user' && typeof m.content === 'string');
  const text = String((first as { content?: string } | undefined)?.content ?? '').trim();
  if (!text) return '';
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
}

function isConversation(value: unknown): value is AssistantConversation {
  const c = value as AssistantConversation | null;
  return !!c && typeof c.id === 'string' && Array.isArray(c.messages);
}

/**
 * Метка времени, которая всегда больше предыдущей.
 *
 * По ней список сортируется и по ней же вытесняется лишнее. Голый
 * `Date.now()` в пределах одной миллисекунды даёт равные значения, и
 * сортировка оставляет их в порядке добавления — то есть вытесняла бы
 * самые свежие переписки вместо самых старых. Тест на десять разговоров
 * подряд это и показал.
 */
let lastStamp = 0;

function now(): number {
  lastStamp = Math.max(Date.now(), lastStamp + 1);
  return lastStamp;
}

/** `crypto.randomUUID` нет в незащищённом контексте — приложение живёт и по http. */
function uid(): string {
  return crypto.randomUUID?.() ?? `chat-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}
